import type {
  ApolloClientOptions,
  NormalizedCacheObject,
} from '@apollo/client/core';
import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import type { ErrorResponse } from '@apollo/client/link/error';
import { onError } from '@apollo/client/link/error';
import { RetryLink } from '@apollo/client/link/retry';
import { logErrorMessages } from '@vue/apollo-util';
import { RestLink } from 'apollo-link-rest';
import camelCase from '@/common/camel-case';
import { router } from '@/plugins/router';
import { RouteName } from '@/router/route-names';
import useLoginData from '@/common/composition/useLoginData';
import useCsrfToken from '@/common/composition/useCsrfToken';
import ZvdZError from '@/common/zvdz-error';
import { getAccessToken } from '@/common/getAccessToken';
import sessionKeyExists from '@/common/sessionKeyExists';

type ErrorListener = (error: ErrorResponse) => void;
const errorListeners: ErrorListener[] = [];
export const setErrorListener = (listener: ErrorListener): void => {
  errorListeners.push(listener);
};

const { host, protocol } = document.location;
const absolutePath = `${protocol}//${host}/api`;

export const apolloCache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        // This type policy is needed, to make deletion of a FeatureFlagLogin work correctly.
        // https://dera.hashnode.dev/fix-cache-data-may-be-lost-when-replacing-the-getallposts-field-of-a-query-object-in-apollo-client
        getAllFeatureFlagLogins: { merge: (_, incoming) => incoming },
        werknemerGeefTaken: { merge: (_, incoming) => incoming },
        werknemerGeefCorrespondentie: { merge: (_, incoming) => incoming },
        twoFactorOrganisations: { merge: (_, incoming) => incoming },
        taken: { merge: (_, incoming) => incoming },
        invoiceList: { merge: (_, incoming) => incoming },
        vervangers: { merge: (_, incoming) => incoming },
        rollenTeVervangen: { merge: (_, incoming) => incoming },
        organisationProtocolDefinitions: { merge: (_, incoming) => incoming },
        users: { merge: (_, incoming) => incoming },
      },
    },
    // TODO: Remove if 'Werknemer/BeoordelingenArts' does not return id's with value '-1'
    VerzuimVerloop: {
      keyFields: ['zhmId', 'beginDatum'],
    },
  },
});

export default function provideDefaultApolloClient() {
  class Status500Error extends ZvdZError {}
  const restLink = new RestLink({
    headers: { 'x-client': 'Apollo' },
    customFetch: (uri: RequestInfo, options: RequestInit) => {
      let extendedUri = uri;
      if (typeof uri === 'string' && options.method === 'GET') {
        const { search } = new URL(uri);
        extendedUri = `${uri}${search ? '&' : '?'}_cb_=${Date.now()}`;
      }

      options.credentials = 'include'; // This is needed to allow the 'Authorization' header to work with CORS.

      const { headers } = options;
      const extendedHeaders = new Headers();
      if (headers && typeof headers.forEach === 'function') {
        headers.forEach((value, key) => {
          if (typeof key !== 'string' || typeof value !== 'string') {
            // eslint-disable-next-line no-console
            console.warn('Unrecognized header', key, value);
            return;
          }
          extendedHeaders.set(key, value);
        });
      }
      const csrfToken = useCsrfToken();
      extendedHeaders.set('X-XSRF-TOKEN', csrfToken);

      const extendedOptions: RequestInit = {
        ...options,
        headers: extendedHeaders,
      };

      return fetch(extendedUri, extendedOptions).then(response => {
        if (
          response.status === 403 ||
          (response.redirected &&
            response.url.includes('/Home/NietGeautoriseerd'))
        ) {
          return new Promise(() => {
            router.replace({ name: RouteName.HomeNietGeautoriseerd });
          });
        }

        if (
          response.status === 401 ||
          (response.redirected && response.url.includes('/Login'))
        ) {
          const { getLogoutRoute } = useLoginData();
          return new Promise(() => {
            // This promise is not resolved to prevent Apollo error
            router.replace(getLogoutRoute(true));
          });
        }

        if (
          response.redirected &&
          response.url.includes('PaginaNietGevonden')
        ) {
          throw new ZvdZError(response);
        }

        if (response.status >= 500 && response.status < 600) {
          throw new Status500Error(response);
        }

        const contentType = response.headers.get('content-type');
        if (contentType && contentType.indexOf('application/json') !== -1) {
          return response
            .clone()
            .json()
            .catch(e => ({ jsonError: String(e) }))
            .then(json => {
              if (
                json.KnownError?.Key === 'NietGeautoriseerd' ||
                json.knownError?.key === 'NietGeautoriseerd'
              ) {
                return new Promise(() => {
                  router.replace({ name: RouteName.HomeNietGeautoriseerd });
                });
              }
              return response;
            });
        }
        return response;
      });
    },
    uri: `${absolutePath}/`,
    fieldNameNormalizer: (key: string) => {
      if (/^\d+$/.test(key)) {
        return `item_${key}`;
      }
      return camelCase(key);
    },
    responseTransformer: async (response: Response) => {
      if (!response || !response.headers) {
        return { result: null };
      }
      const isJson = !!response.headers.get('content-type')?.includes('json');
      if (isJson) {
        const data = await response.json();
        if (
          typeof data === 'boolean' ||
          typeof data === 'string' ||
          typeof data === 'number'
        ) {
          return { result: data };
        }
        return data;
      }
      if (response.redirected) {
        return {
          location: response.url.startsWith(absolutePath)
            ? response.url.substring(absolutePath.length)
            : response.url,
        };
      }
      return response.text();
    },
  });
  const errorLink = onError(error => {
    if (import.meta.env.MODE !== 'production') {
      logErrorMessages(error);
    }
    errorListeners.forEach(listener => {
      listener(error);
    });
  });

  const retryLink = new RetryLink({
    delay: { initial: 1000 },
    attempts: { max: 2, retryIf: error => error instanceof Status500Error },
  });

  const authLink = setContext(async (_, { headers }) => {
    // Indien er (nog) geen Entra authenticatie heeft plaats gevonden, dan geen authorization header toevoegen.
    if (!sessionKeyExists('accesstoken')) {
      return {
        headers,
      };
    }

    // Indien er Entra authenticatie heeft plaats gevonden, dan authorization header toevoegen.
    const token = await getAccessToken();
    return {
      headers: {
        ...headers,
        Authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const clientOptions: ApolloClientOptions<NormalizedCacheObject> = {
    link: ApolloLink.from([errorLink, retryLink, authLink, restLink]),
    cache: apolloCache,
    connectToDevTools: import.meta.env.MODE === 'development',
  };

  const apolloClient = new ApolloClient(clientOptions);

  return apolloClient;
}
